import type { ChartPoint, MarketSignal } from './types';

const MAX_LAST_POINT_AGE_MS = 36 * 60 * 60 * 1000;
const MAX_PRICE_GAP = 0.05;

/**
 * The 90-day chart's last point and the wallet summary's BTC/USD price are
 * two readings of the same "price now", taken a few minutes apart, and
 * showed as two different numbers on one screen. This ends the chart on the
 * summary's reading so the page states one current price.
 *
 * Only the final point changes, and only when it really is "now": it must
 * be recent, and within a few percent of the given price (otherwise the two
 * disagree for some other reason and the chart is left alone).
 */
export function withLatestPrice(
  series: ChartPoint[],
  price: number | null | undefined,
  nowMs: number = Date.now(),
): ChartPoint[] {
  const last = series[series.length - 1];
  if (!last || price === null || price === undefined) return series;
  if (!Number.isFinite(price) || price <= 0 || last.price <= 0) return series;
  if (nowMs - last.t > MAX_LAST_POINT_AGE_MS) return series;
  if (Math.abs(price - last.price) / last.price > MAX_PRICE_GAP) return series;
  if (price === last.price) return series;
  return [...series.slice(0, -1), { ...last, price }];
}

/** The same alignment for a whole signal: its chart and its stated price. */
export function alignSignalPrice(
  signal: MarketSignal,
  price: number | null | undefined,
  nowMs: number = Date.now(),
): MarketSignal {
  const series = withLatestPrice(signal.series, price, nowMs);
  if (series === signal.series) return signal;
  return { ...signal, series, currentPrice: series[series.length - 1].price };
}
